/**
 * 初見デバイス承認 gate (#819 Track B-main Phase 2b).
 *
 * Phase 1 (#825) で発行した device-id の trusted 状態を backend
 * (`/devices/status`, Phase 2a #826) に問い合わせ、**未信頼 device を
 * 承認まで block** するための状態を提供する。承認は登録済 trusted device
 * のアプリ内承認で行う(Google ATO 非依存・物理所持ベース。#819 spec)。
 *
 * gate を実際に出すかは backend flag `deviceApprovalEnabled` (既定 OFF)
 * で RequireAuth 側が制御する(#815 TOTP flag と同方式)。本 hook は flag を
 * 見ない — 状態提供のみ。
 *
 * ⚠️ 重要な安全設計:
 *   - **fail-open**: status 取得失敗 / device-id 取得不可は「block しない」側
 *     (trusted 扱い)に倒す。auth gate を network 一時障害で brick しない
 *     (useDeviceConfirmGate と同方針)。
 *   - **block 述語は `exists && !trusted && !denied`**。exists=false
 *     (= Phase 1 の best-effort 記録がまだ/失敗。device が未記録)は
 *     **block しない**。記録済かつ未信頼の正真の新端末のみ block。
 *     denied は waiting modal を出さず、credential 失効済なので
 *     RequireAuth が enrollment gate に流す。
 *   - waiting 中は poll し、trusted 端末側で承認されたら自動解除。
 *
 * 設計: useDeviceConfirmGate と同じ useSyncExternalStore + 不変 snapshot。
 */
import { useEffect, useSyncExternalStore } from 'react'
import { useStore } from '../store/useStore'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'

type DeviceStatus = { exists: boolean; trusted: boolean; denied: boolean }

type GateState = {
  status: DeviceStatus | null // null = 未評価
  loading: boolean
}

// fail-open の安全側 default (block しない)。
const SAFE_OPEN: DeviceStatus = { exists: false, trusted: true, denied: false }

let state: GateState = { status: null, loading: false }
const listeners = new Set<() => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): GateState {
  return state
}

function isWaiting(s: DeviceStatus | null): boolean {
  // block するのは「記録済 & 未信頼 & 未否認」のみ。それ以外は通す。
  return !!s && s.exists === true && s.trusted === false && s.denied === false
}

function statusEqual(a: DeviceStatus | null, b: DeviceStatus | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.exists === b.exists && a.trusted === b.trusted && a.denied === b.denied
  )
}

function setState(next: Partial<GateState>): void {
  // #832: status は evaluate() が poll 毎に新 object を作るため参照比較だと
  // 値不変でも毎回 re-render する churn になる。**値比較**で dedup。
  if (
    (next.status === undefined || statusEqual(next.status, state.status)) &&
    (next.loading === undefined || next.loading === state.loading)
  ) {
    return
  }
  // useSyncExternalStore は Object.is 比較なので必ず新 object を割当てる
  // (in-place 変更だと markDeviceApproved 後に modal が閉じない)。
  state = {
    status: next.status !== undefined ? next.status : state.status,
    loading: next.loading !== undefined ? next.loading : state.loading,
  }
  for (const l of listeners) l()
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function ensurePolling(): void {
  if (pollTimer) return
  // 承認は別端末で行われるため poll で自動解除する。4s 間隔。
  pollTimer = setInterval(() => {
    void evaluate()
  }, 4000)
}

let fetchInFlight = false

async function evaluate(): Promise<void> {
  if (fetchInFlight) return
  fetchInFlight = true
  // #832: loading トグルは **初回 fetch のみ**。background poll で
  // true→false すると(値不変でも)余計な re-render churn になる。
  if (state.status === null) setState({ loading: true })
  try {
    let did = ''
    try {
      did = getDeviceId()
    } catch {
      did = ''
    }
    if (!did) {
      // device-id 取れない → fail-open (block しない)
      stopPolling()
      setState({ status: SAFE_OPEN, loading: false })
      return
    }
    const s = await api.getDeviceStatus(did).catch(() => null)
    if (!s) {
      // 取得失敗 → fail-open。poll は続け、復旧したら正しい状態に収束。
      setState({ status: SAFE_OPEN, loading: false })
      return
    }
    const status: DeviceStatus = {
      exists: !!s.exists,
      trusted: !!s.trusted,
      denied: !!s.denied,
    }
    setState({ status, loading: false })
    if (isWaiting(status)) ensurePolling()
    else stopPolling()
  } catch {
    // fail-open
    stopPolling()
    setState({ status: SAFE_OPEN, loading: false })
  } finally {
    fetchInFlight = false
  }
}

/** trusted 端末で承認された後の楽観セット (waiting modal を即 unmount). */
export function markDeviceApproved(): void {
  stopPolling()
  setState({ status: { exists: true, trusted: true, denied: false }, loading: false })
}

/** Logout 時に呼ぶ — cache クリア + poll 停止 (別ユーザーで再評価). */
export function resetDeviceApprovalGate(): void {
  stopPolling()
  setState({ status: null, loading: false })
}

/** 手動再確認 (待機 modal のボタン用。auto-poll とは別に即時 evaluate). */
export function recheckDeviceApproval(): void {
  void evaluate()
}

export function useDeviceApprovalGate(enabled: boolean): {
  /** block すべき状態か (= 記録済・未信頼・未否認)。!enabled は常に false。 */
  requiresApproval: boolean
  /** この端末は trusted か。 */
  trusted: boolean
  /** 否認済 (credential 失効済 → 呼び元は enrollment gate に流す)。 */
  denied: boolean
  loading: boolean
} {
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    // flag OFF (= !enabled) は fetch も poll もしない = byte-for-byte 不変。
    // enabled が落ちた / logout は cache クリア + poll 停止。
    if (!enabled || !isLoggedIn) {
      if (snapshot.status !== null) resetDeviceApprovalGate()
      return
    }
    if (snapshot.status === null && !snapshot.loading) {
      void evaluate()
    }
  }, [enabled, isLoggedIn, snapshot.status, snapshot.loading])

  const s = enabled ? snapshot.status : null
  return {
    requiresApproval: enabled && isWaiting(s),
    trusted: !!s && s.trusted === true,
    denied: !!s && s.denied === true,
    loading: enabled ? snapshot.loading : false,
  }
}
